/**
 * Simple utility to parse current URL and return PageContext
 * No React hooks - just reads window.location and parses it
 */

import { BotPageContext } from '@/types/ai-bot-framework';

export function getCurrentPageContext(): BotPageContext {
  // In SSR context, return default
  if (typeof window === 'undefined') {
    return {
      page: 'copilot',
      metadata: {}
    };
  }

  const pathname = window.location.pathname;

  // Transcript pages: /transcripts/[id]
  const transcriptMatch = pathname.match(/^\/transcripts\/([^\/]+)$/);
  if (transcriptMatch) {
    return {
      page: 'transcript',
      metadata: {
        transcriptId: transcriptMatch[1],
        pathname
      }
    };
  }

  // QA Evaluation pages: /qa/evaluations/[id]
  const qaEvalMatch = pathname.match(/^\/qa\/evaluations\/([^\/]+)$/);
  if (qaEvalMatch) {
    return {
      page: 'qa-evaluation',
      metadata: {
        evaluationId: qaEvalMatch[1],
        pathname
      }
    };
  }

  // QA Form pages: /qa/forms/[id]
  const qaFormMatch = pathname.match(/^\/qa\/forms\/([^\/]+)/);
  if (qaFormMatch) {
    // Check if it's the builder
    if (pathname.includes('/builder')) {
      return {
        page: 'qa-form-builder',
        metadata: {
          mode: qaFormMatch[1] ? 'edit' : 'create',
          formId: qaFormMatch[1] || undefined,
          pathname
        }
      };
    }

    return {
      page: 'qa-form',
      metadata: {
        formId: qaFormMatch[1],
        pathname
      }
    };
  }

  // QA Form Builder: /qa/forms/builder
  if (pathname.includes('/qa/forms/builder')) {
    return {
      page: 'qa-form-builder',
      metadata: {
        mode: 'create',
        pathname
      }
    };
  }

  // Entity pages: /entities/[id]
  const entityMatch = pathname.match(/^\/entities\/([^\/]+)$/);
  if (entityMatch) {
    return {
      page: 'entity',
      metadata: {
        entityId: entityMatch[1],
        pathname
      }
    };
  }

  // Integration pages: /integrations/[id]
  const integrationMatch = pathname.match(/^\/integrations\/([^\/]+)$/);
  if (integrationMatch) {
    return {
      page: 'integration',
      metadata: {
        integrationId: integrationMatch[1],
        pathname
      }
    };
  }

  // Rules pages: /rules/edit/[id], /rules/view/[id], /rules/test/[id]
  const ruleMatch = pathname.match(/^\/rules\/(edit|view|test|create)\/([^\/]+)$/);
  if (ruleMatch) {
    return {
      page: 'rule',
      metadata: {
        ruleId: ruleMatch[2],
        mode: ruleMatch[1] as 'edit' | 'view' | 'test' | 'create',
        pathname
      }
    };
  }

  // Analytics pages: /analytics and /analytics/ai
  if (pathname.startsWith('/analytics')) {
    return {
      page: 'analytics',
      metadata: {
        view: pathname.includes('/ai') ? 'ai-chat' : 'dashboard',
        pathname
      }
    };
  }

  // Dashboard: /dashboard
  if (pathname === '/dashboard') {
    return {
      page: 'dashboard',
      metadata: {
        view: 'overview',
        pathname
      }
    };
  }

  // Signals: /signals
  if (pathname.startsWith('/signals')) {
    return {
      page: 'signals',
      metadata: {
        view: 'generator',
        pathname
      }
    };
  }

  // Default/Copilot - when no specific page is detected
  return {
    page: 'copilot',
    metadata: {
      currentRoute: pathname || '/',
      pathname: pathname || '/'
    }
  };
}
